import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getPincodeDropValues,
  getVillageDropValues,
  getVillageGridDropValues,
  getZonesDropValues,
} from '../services/dropDownService';
import {
  MapOptionType,
  LocationFilterInitialState,
  SelectOptionType,
} from '../utils/Types';
import { processDistricts } from '../utils/Util';

const initialState: LocationFilterInitialState = {
  zoneOptions: [],
  statesData: {},
  districtData: {},
  cityOptions: {},
  gridOptions: [],
};

export const getZonesAndStates = createAsyncThunk(
  'locationFilter/zones',
  async (data: any) => {
    const res = await getZonesDropValues(data);
    return res?.data;
  }
);

export const getPincodeDropData = createAsyncThunk(
  'locationFilter/pincode',
  async (data: any) => {
    const modifiedData = {
      pincode: data?.length ? data : '',
    };
    const res = await getPincodeDropValues(modifiedData);
    return res?.data;
  }
);

export const getAllDistrictOfStates = createAsyncThunk(
  'locationFilter/district',
  async (data: any) => {
    const res = await getZonesDropValues(data);
    return res?.data;
  }
);
export const fetchVillagesOfDistricts = createAsyncThunk(
  'locationFilter/fetchVillageDrops',
  async (data: any) => {
    const modifiedData = {
      districtId: data?.districtId?.length > 0 ? data.districtId.join(',') : '',
    };
    const res = await getVillageDropValues(modifiedData);
    return res.data;
  }
);
export const fetchGridsOfVillage = createAsyncThunk(
  'locationFilter/fetchGridsOfVillage',
  async (data: any) => {
    const res = await getVillageGridDropValues(data);
    return res.data;
  }
);
export const locationFilterSlice = createSlice({
  name: 'locationFilter',
  initialState,
  reducers: {
    clearGrids: (state) => {
      state.gridOptions = [];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getZonesAndStates.fulfilled, (state, action) => {
        const rawData = action?.payload?.data?.[0] ?? [];

        const zonesMap: Record<
          string,
          {
            zoneId: string;
            zoneName: string;
            states: {
              stateId: string;
              stateName: string;
              districts?: any[];
            }[];
          }
        > = {};

        rawData.forEach((item: any) => {
          const {
            zoneId,
            zoneName,
            stateId,
            stateName,
            districtId,
            districtName,
          } = item;

          if (!zonesMap[zoneId]) {
            zonesMap[zoneId] = {
              zoneId: zoneId.toString(),
              zoneName,
              states: [],
            };
          }

          const zone = zonesMap[zoneId];

          let state = zone.states.find((s) => s.stateId === stateId.toString());

          if (!state) {
            state = {
              stateId: stateId.toString(),
              stateName,
              districts: [],
            };
            zone.states.push(state);
          }

          if (districtId && districtName) {
            state.districts?.push({
              districtId: districtId.toString(),
              districtName,
            });
          }
        });

        const zonesData = Object.values(zonesMap);

        if (zonesData.length > 0) {
          const zoneOption: SelectOptionType[] = [];
          const allStates: MapOptionType = {};
          const allDistricts: MapOptionType = {};

          zonesData.forEach((zone) => {
            zoneOption.push({
              value: zone.zoneId,
              label: zone.zoneName,
            });

            const stateOption: SelectOptionType[] = [];

            zone.states.forEach((state) => {
              if (state?.districts?.length) {
                const districtOption = processDistricts(state.districts);
                allDistricts[state.stateId] = districtOption;
              }

              stateOption.push({
                value: state.stateId,
                label: state.stateName,
              });
            });

            allStates[zone.zoneId] = stateOption;
          });

          state.zoneOptions = zoneOption;
          state.statesData = allStates;
          state.districtData = allDistricts;
        }
      })
      .addCase(getPincodeDropData.fulfilled, (state, action) => {})
      .addCase(getAllDistrictOfStates.fulfilled, (state, action) => {
        if (action?.payload?.data?.length) {
          const rawDistricts = action.payload.data[0];
          const stateMap: any = {};

          rawDistricts.forEach((item: any) => {
            const { stateId, stateName, districtId, districtName } = item;

            if (!stateMap[stateId]) {
              stateMap[stateId] = {
                stateId: stateId.toString(),
                stateName,
                districts: [],
              };
            }

            stateMap[stateId].districts.push({
              districtId: districtId.toString(),
              districtName,
            });
          });

          const states = Object.values(stateMap);
          const allDistrict: MapOptionType = {};

          if (states.length > 0) {
            states.forEach((item: any) => {
              const districtOptions: SelectOptionType[] = item.districts.map(
                (element: { districtId: any; districtName: any }) => ({
                  value: element.districtId,
                  label: element.districtName,
                })
              );
              allDistrict[item.stateId] = districtOptions;
            });
            state.districtData = allDistrict;
          }
        }
      })
      .addCase(fetchVillagesOfDistricts.fulfilled, (state, action) => {
        if (action?.payload?.length) {
          const responseData = action.payload.flat();
          const districtMap: any = {};

          responseData.forEach((item: any) => {
            const districtId = item.districtId;

            if (!districtMap[districtId]) {
              districtMap[districtId] = {
                districtId: districtId.toString(),
                districtName: item.districtName,
                villages: [],
              };
            }

            districtMap[districtId].villages.push({
              mandalName: item.mandalName,
              pincode: item.pincodeId,
              villageName: item.villageName,
              villageId: item.villageId.toString(),
            });
          });

          const districts = Object.values(districtMap);

          if (districts.length > 0) {
            districts.forEach((district: any) => {
              const villageOptions: SelectOptionType[] = [];
              district.villages.forEach(
                (element: {
                  villageId: string;
                  villageName: string;
                  mandalName: string | null;
                  pincode: string | number;
                }) => {
                  villageOptions.push({
                    value: element.villageId,
                    label: element.mandalName
                      ? `${element.mandalName} - ${element.villageName}`
                      : element.villageName,
                    pincode: element.pincode,
                  });
                }
              );
              state.cityOptions[district.districtId] = villageOptions;
            });
          }

          action.payload.response = { districts };
        }
      })
      .addCase(fetchGridsOfVillage.fulfilled, (state, action) => {
        if (action.payload.response && action.payload.response.length > 0) {
          const response = action.payload.response;
          if (response && Array.isArray(response) && response.length > 0) {
            const array: SelectOptionType[] = [];
            response.forEach(
              (element: { gridid: string; vname: string; pincode: number }) => {
                array.push({
                  value: element.gridid,
                  label: element.gridid,
                });
              }
            );
            state.gridOptions = array;
          } else {
            state.gridOptions = [];
          }
        }
      });
  },
});
export const locationFilterSelector = (state: {
  locationFilter: LocationFilterInitialState;
}) => state.locationFilter;

export const { clearGrids } = locationFilterSlice.actions;
export default locationFilterSlice.reducer;
